import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  UserPlusIcon,
  UserIcon,
  CreditCardIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import accountService from '../../services/accountService';
import agencyService from '../../services/agencyService';
import clientService from '../../services/clientService';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useToast } from '../../contexts/ToastContext';

const emptyClient = {
  first_name: '',
  last_name: '',
  gender: '',
  date_of_birth: '',
  national_id: '',
  phone: '',
  email: '',
  address: '',
  occupation: '',
  monthly_income: '',
  agency_id: '',
};

const emptyAccount = {
  account_type: 'regular',
  interest_rate: '2.5',
  minimum_balance: '0',
};

const accountTypes = [
  { value: 'regular', label: 'Épargne régulière' },
  { value: 'fixed', label: 'Dépôt à terme' },
  { value: 'youth', label: 'Épargne jeune' },
  { value: 'business', label: 'Épargne entreprise' },
];

const AccountForm = () => {
  const navigate = useNavigate();
  const { currency } = useCurrency();
  const { showSuccess, showError } = useToast();
  const [mode, setMode] = useState('existing');
  const [agencies, setAgencies] = useState([]);
  const [clientForm, setClientForm] = useState(emptyClient);
  const [accountForm, setAccountForm] = useState(emptyAccount);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const fetchAgencies = async () => {
      try {
        const data = await agencyService.getAllAgencies();
        setAgencies(data || []);
      } catch (error) {
        showError('Erreur lors du chargement des agences');
      }
    };

    fetchAgencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClientChange = (event) => {
    const { name, value } = event.target;
    setClientForm((previous) => ({ ...previous, [name]: value }));
  };

  const handleAccountChange = (event) => {
    const { name, value } = event.target;
    setAccountForm((previous) => ({ ...previous, [name]: value }));
  };

  const searchClient = async () => {
    if (query.trim().length < 3) {
      showError('Entrez au moins 3 caractères pour rechercher');
      return;
    }

    try {
      setSearching(true);
      setSelectedClient(null);
      const data = await clientService.lookupForAccount(query.trim());
      setResults(data.clients || []);
    } catch (error) {
      showError(error.response?.data?.message || 'Erreur lors de la recherche du client');
    } finally {
      setSearching(false);
    }
  };

  const validateClient = () => {
    const requiredFields = [
      'first_name',
      'last_name',
      'gender',
      'date_of_birth',
      'national_id',
      'phone',
      'address',
      'occupation',
      'monthly_income',
      'agency_id',
    ];

    return requiredFields.every((field) => String(clientForm[field] || '').trim());
  };

  const createAccountForClient = async (clientId) => {
    return accountService.createAccount({
      client_id: clientId,
      account_type: accountForm.account_type,
      interest_rate: parseFloat(accountForm.interest_rate || 0),
      minimum_balance: parseFloat(accountForm.minimum_balance || 0),
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      let clientId = selectedClient?.id;

      if (mode === 'new') {
        if (!validateClient()) {
          showError('Complétez les champs obligatoires du client');
          return;
        }

        const client = await clientService.createClient({
          ...clientForm,
          monthly_income: parseFloat(clientForm.monthly_income || 0),
        });
        clientId = client.id;
      }

      if (!clientId) {
        showError('Sélectionnez un client existant ou créez un nouveau client');
        return;
      }

      const account = await createAccountForClient(clientId);
      showSuccess(`Compte ${account.account_number} créé avec succès`);
      navigate('/accounts');
    } catch (error) {
      showError(error.response?.data?.message || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setSelectedClient(null);
    setResults([]);
    setQuery('');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Nouveau compte</h1>
          <p className="mt-1 text-sm text-gray-600">
            Ouvrez un compte pour un nouveau client ou pour un client déjà enregistré
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => switchMode('existing')}
            className={`flex items-center gap-3 rounded-lg border p-4 text-left ${
              mode === 'existing' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:bg-gray-50'
            }`}
          >
            <UserIcon className="h-6 w-6 text-primary-600" />
            <span>
              <span className="block font-medium text-gray-900">Client existant</span>
              <span className="block text-sm text-gray-500">Recherche par compte, code client ou pièce</span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => switchMode('new')}
            className={`flex items-center gap-3 rounded-lg border p-4 text-left ${
              mode === 'new' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:bg-gray-50'
            }`}
          >
            <UserPlusIcon className="h-6 w-6 text-primary-600" />
            <span>
              <span className="block font-medium text-gray-900">Nouveau client</span>
              <span className="block text-sm text-gray-500">Créer le client puis ouvrir son compte</span>
            </span>
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {mode === 'existing' ? (
              <div className="card">
                <div className="p-6 space-y-5">
                  <div>
                    <h2 className="text-lg font-medium text-gray-900">Rechercher le client</h2>
                    <p className="mt-1 text-sm text-gray-600">
                      Utilisez le numéro de compte, le code client ou le numéro de pièce d'identité.
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          searchClient();
                        }
                      }}
                      className="input-field"
                      placeholder="Ex: SA-000001, CL-1-000001, CIN..."
                    />
                    <button type="button" onClick={searchClient} className="btn-primary" disabled={searching}>
                      <MagnifyingGlassIcon className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="space-y-3">
                    {results.map((client) => (
                      <button
                        key={client.id}
                        type="button"
                        onClick={() => setSelectedClient(client)}
                        className={`w-full rounded-lg border p-4 text-left ${
                          selectedClient?.id === client.id
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="font-medium text-gray-900">
                              {client.first_name} {client.last_name}
                            </p>
                            <p className="mt-1 text-sm text-gray-600">
                              Code: {client.client_number} · Pièce: {client.national_id || '-'}
                            </p>
                            <p className="mt-1 text-sm text-gray-600">Téléphone: {client.phone || '-'}</p>
                            {client.accounts?.length > 0 && (
                              <p className="mt-1 text-xs text-gray-500">
                                Comptes: {client.accounts.map((account) => account.account_number).join(', ')}
                              </p>
                            )}
                          </div>
                          {selectedClient?.id === client.id && (
                            <CheckCircleIcon className="h-6 w-6 flex-shrink-0 text-primary-600" />
                          )}
                        </div>
                      </button>
                    ))}
                    {!searching && query && results.length === 0 && (
                      <p className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                        Aucun client trouvé pour cette recherche.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="card">
                <div className="p-6 space-y-6">
                  <div>
                    <h2 className="text-lg font-medium text-gray-900">Informations du client</h2>
                    <p className="mt-1 text-sm text-gray-600">Les champs marqués d'un astérisque sont requis.</p>
                  </div>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <TextField label="Prénom *" name="first_name" value={clientForm.first_name} onChange={handleClientChange} />
                    <TextField label="Nom *" name="last_name" value={clientForm.last_name} onChange={handleClientChange} />
                    <SelectField label="Genre *" name="gender" value={clientForm.gender} onChange={handleClientChange}>
                      <option value="">Sélectionnez</option>
                      <option value="male">Homme</option>
                      <option value="female">Femme</option>
                      <option value="other">Autre</option>
                    </SelectField>
                    <TextField label="Date de naissance *" name="date_of_birth" type="date" value={clientForm.date_of_birth} onChange={handleClientChange} />
                    <TextField label="Pièce d'identité *" name="national_id" value={clientForm.national_id} onChange={handleClientChange} />
                    <TextField label="Téléphone *" name="phone" value={clientForm.phone} onChange={handleClientChange} />
                    <TextField label="Email" name="email" type="email" value={clientForm.email} onChange={handleClientChange} />
                    <TextField label="Profession *" name="occupation" value={clientForm.occupation} onChange={handleClientChange} />
                    <TextField label={`Revenu mensuel (${currency}) *`} name="monthly_income" type="number" value={clientForm.monthly_income} onChange={handleClientChange} />
                    <SelectField label="Agence *" name="agency_id" value={clientForm.agency_id} onChange={handleClientChange}>
                      <option value="">Sélectionnez une agence</option>
                      {agencies.map((agency) => (
                        <option key={agency.id} value={agency.id}>{agency.name}</option>
                      ))}
                    </SelectField>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Adresse *</label>
                    <textarea
                      name="address"
                      rows={3}
                      value={clientForm.address}
                      onChange={handleClientChange}
                      className="input-field mt-1"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="card">
              <div className="p-6 space-y-5">
                <div className="flex items-center gap-3">
                  <CreditCardIcon className="h-6 w-6 text-primary-600" />
                  <h2 className="text-lg font-medium text-gray-900">Paramètres du compte</h2>
                </div>
                <SelectField label="Type de compte" name="account_type" value={accountForm.account_type} onChange={handleAccountChange}>
                  {accountTypes.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </SelectField>
                <TextField
                  label="Taux d'intérêt annuel (%)"
                  name="interest_rate"
                  type="number"
                  step="0.01"
                  value={accountForm.interest_rate}
                  onChange={handleAccountChange}
                />
                <TextField
                  label={`Solde minimum (${currency})`}
                  name="minimum_balance"
                  type="number"
                  step="0.01"
                  value={accountForm.minimum_balance}
                  onChange={handleAccountChange}
                />
                {selectedClient && (
                  <div className="rounded-lg bg-gray-50 p-3 text-sm">
                    <p className="font-medium text-gray-900">Client sélectionné</p>
                    <p className="mt-1 text-gray-600">{selectedClient.first_name} {selectedClient.last_name}</p>
                    <p className="text-gray-500">{selectedClient.client_number}</p>
                  </div>
                )}
                <div className="flex gap-3 border-t border-gray-200 pt-5">
                  <button type="button" onClick={() => navigate('/accounts')} className="btn-secondary flex-1" disabled={loading}>
                    Annuler
                  </button>
                  <button type="submit" className="btn-primary flex-1" disabled={loading}>
                    {loading ? 'Création...' : 'Créer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

const TextField = ({ label, name, value, onChange, type = 'text', step }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      step={step}
      className="input-field mt-1"
    />
  </div>
);

const SelectField = ({ label, name, value, onChange, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <select name={name} value={value} onChange={onChange} className="input-field mt-1">
      {children}
    </select>
  </div>
);

export default AccountForm;
